"use client";

import { useEffect, useRef } from "react";
import type { CarouselApi } from "@workspace/ui/components/carousel";
import { EventNames, sendEvent, withSectionType } from "@/lib/utils/analytics";
import { DEFAULT_SCROLL_PERCENTAGE_THRESHOLDS } from "./scroll-percentage-thresholds";

// A slide change within this long of user input on the section counts as the user's scroll;
// anything later (autoplay advancing on its own) doesn't.
const USER_SCROLL_WINDOW_MS = 1000;
const USER_INPUT_EVENTS = ["pointerdown", "keydown", "click", "wheel"] as const;

interface UseEmblaHorizontalScrollAnalyticsOptions {
  api: CarouselApi | undefined;
  sectionType?: string;
  /**
   * Scroll-depth milestones (0–100), same idea as {@link PageScrollEvents}.
   * Fires once per milestone when Embla scroll progress reaches it.
   */
  thresholds?: readonly number[];
  eventName?: string;
  eventProperties?: Record<string, unknown>;
}

/**
 * Fires `section_scrolled` once per scroll-depth milestone, plus `horizontal_section_scrolled`
 * once per slide the user moves to (`slide_screen`, 1-based).
 */
export function useEmblaHorizontalScrollAnalytics({
  api,
  sectionType,
  thresholds = DEFAULT_SCROLL_PERCENTAGE_THRESHOLDS,
  eventName = EventNames.SECTION_SCROLLED,
  eventProperties,
}: UseEmblaHorizontalScrollAnalyticsOptions) {
  const triggeredRef = useRef<Set<number>>(new Set());
  const viewedSlidesRef = useRef<Set<number>>(new Set());

  useEffect(() => {
    triggeredRef.current = new Set();
    viewedSlidesRef.current = new Set();
  }, [api]);

  useEffect(() => {
    if (!api || !sectionType) return;

    const trackScroll = () => {
      const progress = api.scrollProgress();
      const percent = Math.min(100, progress * 100);

      for (const t of thresholds) {
        if (percent >= t && !triggeredRef.current.has(t)) {
          triggeredRef.current.add(t);
          sendEvent(
            eventName,
            withSectionType(sectionType, {
              percentage_scrolled: t,
              ...eventProperties,
            }),
          );
        }
      }
    };

    api.on("scroll", trackScroll);
    api.on("select", trackScroll);
    trackScroll();

    return () => {
      api.off("scroll", trackScroll);
      api.off("select", trackScroll);
    };
  }, [api, eventName, eventProperties, sectionType, thresholds]);

  // Only slide changes the user caused (swipe, drag, dots, arrows, keys, trackpad) count;
  // autoplay advances are ignored.
  useEffect(() => {
    if (!api || !sectionType) return;

    // Controls such as dots can sit outside the carousel, so listen across the whole section.
    const root = api.rootNode();
    const area = root.closest("section") ?? root;
    let lastInputAt = -Infinity;
    const markInput = () => {
      lastInputAt = performance.now();
    };

    const trackSlide = () => {
      if (performance.now() - lastInputAt > USER_SCROLL_WINDOW_MS) return;
      // The slide the user just moved to.
      const slide = api.selectedScrollSnap() + 1;
      if (viewedSlidesRef.current.has(slide)) return;
      viewedSlidesRef.current.add(slide);
      sendEvent(
        EventNames.HORIZONTAL_SECTION_SCROLLED,
        withSectionType(sectionType, { slide_screen: slide }),
      );
    };

    USER_INPUT_EVENTS.forEach((type) =>
      area.addEventListener(type, markInput, { capture: true, passive: true }),
    );
    // A drag can settle on its new slide after the pointer is released.
    api.on("pointerUp", markInput);
    api.on("select", trackSlide);

    return () => {
      USER_INPUT_EVENTS.forEach((type) =>
        area.removeEventListener(type, markInput, { capture: true }),
      );
      api.off("pointerUp", markInput);
      api.off("select", trackSlide);
    };
  }, [api, sectionType]);
}
